import { Box, ButtonGroup, Grid, Link, Typography, useTheme } from '@mui/material'
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useErrorDisplay } from '../components/ErrorDisplay';
import AssessmentClient, { AssessmentDisplay, AssessmentErrors } from '../apiClients/AssessmentClient'
import BusySpinner from '../components/BusySpinner'
import { useWindowDimensions } from '../components/WindowDimensions'
import { ExtractForSetError, FormatDateTime } from '../Helpers'
import AssessmentDisplayPopup from '../popups/AssessmentDisplayPopup'
import useViewportMode from '../hooks/useViewportMode'
import { MobileEntityCard, ResponsiveCollection } from '../components/responsive'

export interface AssessmentListComponentProps {
    talentId: string;
    subscriptionActive: boolean;
}

export const AssessmentListComponent = ({ talentId, subscriptionActive }: AssessmentListComponentProps) => {

    const { height } = useWindowDimensions();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const theme = useTheme();
    const viewport = useViewportMode();

    const [assessments, setAssessments] = useState<AssessmentDisplay[]>([])

    const loadData = async () => {
        try {
            setBusy(true);

            const apiResponse = await new AssessmentClient().GetAllByTalent(talentId);
            if (ExtractForSetError(apiResponse, AssessmentErrors.GetAll, setError))
                return;

            setAssessments(apiResponse.Content);

        } catch (error) {
            setError(error, AssessmentErrors.GetAll);
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {

        if (subscriptionActive)
            loadData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [talentId, subscriptionActive])

    const getAlternateDisplayGridContainerStyle = () => {
        return { width: '100%', marginTop: theme.spacing(0) };
    }

    if (!subscriptionActive)
        if (!viewport.isDesktop)
            return <Box sx={{ p: 2 }}><Typography variant="body2">You need an active subscription for Assessments, <Link href="/SubscriptionPage" target="_self" rel="noopener" underline="hover">subscribe here</Link></Typography></Box>;
        else
        return (
            <>
                <Grid container spacing={1} sx={getAlternateDisplayGridContainerStyle}>
                    <Grid item xs={3}></Grid>
                    <Grid item xs={9}>
                        <Typography sx={{ fontSize: 'small', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}>
                            {`You need an active subscription for Assessments, `}
                            <Link href="/SubscriptionPage" target="_self" rel="noopener" underline="hover">{`subscribe here`}</Link>
                        </Typography>

                    </Grid>
                </Grid>
            </>
        )
    else if (assessments.length <= 0)
        if (!viewport.isDesktop)
            return <Typography variant="body2" sx={{ p: 2 }}>Haven't performed an Assessment... yet</Typography>;
        else
        return (
            <>
                <Grid container spacing={1} sx={{ width: '100%', marginTop: theme.spacing(0) }}>
                    <Grid item xs={3}></Grid>
                    <Grid item xs={9}>
                        <Typography sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(2), fontSize: 'small' }}>Haven't performed an Assessment... yet</Typography>
                    </Grid>
                </Grid>

            </>
        )
    else
        return (
            <>
                <ErrorDisplay />
                <BusySpinner busy={busy}>
                    <ResponsiveCollection
                        items={assessments}
                        getKey={(assessment) => assessment.Id}
                        viewMode={viewport.mode}
                        ariaLabel="Assessments"
                        renderCard={(assessment) => (
                            <MobileEntityCard
                                title={assessment.TemplateName}
                                subtitle={FormatDateTime(dayjs(assessment.CreatedDate).toString())}
                                fields={[{ label: 'Assessor', value: assessment.AssessorName, fullWidth: true }]}
                                actions={<AssessmentDisplayPopup assessmentId={assessment.Id} />}
                                ariaLabel={`Assessment ${assessment.TemplateName}`}
                            />
                        )}
                        desktop={<div style={{ height: height - 300, width: '100%' }}>
                        <DataGridPro
                            density="compact"
                            rows={(assessments).map(x => { return { ...x, id: x.Id } })}
                            columns={[
                                {
                                    field: 'Action',
                                    headerName: '',
                                    sortable: false,
                                    filterable: false,
                                    resizable: false,
                                    width: 70,
                                    align: 'center',
                                    disableColumnMenu: true,
                                    renderCell: ({ row: a }) =>
                                        <ButtonGroup>
                                            <AssessmentDisplayPopup assessmentId={a.Id} />
                                        </ButtonGroup>
                                },
                                {
                                    field: 'TemplateName',
                                    headerName: 'Template',
                                    flex: 1
                                },
                                {
                                    field: 'AssessorName',
                                    headerName: 'Assessor',
                                    flex: 1,
                                },
                                {
                                    field: 'CreatedDate',
                                    headerName: 'Created Date',
                                    flex: 1,
                                    renderCell: (params) => FormatDateTime(dayjs(params.value).toString())
                                }
                            ]}
                            components={{
                                Toolbar: GridToolbar
                            }}
                        />
                        </div>}
                    />
                </BusySpinner>
            </>
        )
}

export default AssessmentListComponent;
